import { ERoleAdminString } from 'src/app/shared/data/role.enum';

export interface ISidebarMenuItem {
  label: string;
  route: string;
  icon: string;
  permissions: string[];
  hasNotification?: boolean;
  /**
   * Item restrito ao uso interno Oliv-e: exige TODAS as permissões listadas e o flag
   * "Admin Oliv-e" do perfil de acesso. Sem isto, `permissions` é avaliado como "qualquer uma",
   * e a role sozinha já liberaria o item.
   */
  restrito?: boolean;
}

export interface ISidebarMenuGroup {
  title: string;
  items: ISidebarMenuItem[];
}

// fonte unica dos itens de menu: reusada pelo SidebarComponent (navegacao) e pelo
// InicioComponent (resolver label/icone das ultimas paginas visitadas)
export const MENU_GROUPS: ISidebarMenuGroup[] = [
  {
    title: 'Visão Geral',
    items: [
      {
        label: 'Dashboard',
        route: 'dashboard',
        icon: 'layout-dashboard',
        permissions: [ERoleAdminString.Admin, 'dashboard.list', 'dashboard.view']
      },
      {
        label: 'Configurar Meu Dashboard',
        route: 'my-dashboard',
        icon: 'sliders-horizontal',
        // só a role Admin (exibida como "Administrador Oliv-e"): sem a claim no array,
        // porque o "qualquer uma" do isItemVisible deixaria a claim liberar o item sozinha
        permissions: [ERoleAdminString.Admin, 'dashboard.list']
      }
    ]
  },
  {
    title: 'Consultas & Atendimento',
    items: [
      {
        label: 'Agenda',
        route: 'agenda',
        icon: 'calendar',
        permissions: [ERoleAdminString.Doctor, 'appointments.switchdoctor']
      },
      {
        label: 'Solicitações de Consultas',
        route: 'consultas',
        icon: 'calendar-clock',
        permissions: [ERoleAdminString.Doctor, 'appointments.list'],
        hasNotification: true
      },
      {
        label: 'Consultas Agendadas',
        route: 'proximas-consultas',
        icon: 'calendar-check',
        permissions: [ERoleAdminString.Doctor, 'appointments.list']
      },
      {
        label: 'Histórico de Consultas',
        route: 'historico',
        icon: 'history',
        permissions: [ERoleAdminString.Doctor, 'appointments.list']
      },
      {
        label: 'Atestados',
        route: 'atestados',
        icon: 'file-text',
        permissions: [ERoleAdminString.Doctor, 'sicknote.list']
      },
      { label: 'Pacientes', route: 'pacientes', icon: 'users', permissions: ['patients.list'] },
      {
        label: 'Atendimento',
        route: 'user-support',
        icon: 'headphones',
        permissions: ['user-support.list', ERoleAdminString.Doctor]
      },
      { label: 'Filas', route: 'filas', icon: 'list-ordered', permissions: [ERoleAdminString.Admin, 'queue.list'] },
      {
        label: 'Motivos de Atendimento',
        route: 'motivos-atendimento',
        icon: 'info',
        permissions: [ERoleAdminString.Admin, 'reason.list']
      }
    ]
  },
  {
    title: 'Relatório',
    items: [
      {
        label: 'Atendimentos Realizados',
        route: 'relatorios',
        icon: 'bar-chart-3',
        permissions: [ERoleAdminString.Admin, 'reports.list']
      },
      {
        label: 'Solicitações de Atendimento',
        route: 'solicitacoes-atendimento',
        icon: 'clipboard-list',
        permissions: [ERoleAdminString.Admin, 'request-reports.list']
      },
      {
        label: 'Colaboradores',
        route: 'relatorio-colaboradores',
        icon: 'credit-card',
        permissions: [ERoleAdminString.Admin, 'collaborator-reports.list']
      }
    ]
  },
  {
    title: 'Gestão Organizacional',
    items: [
      {
        label: 'Empresas',
        route: 'empresas',
        icon: 'building-2',
        permissions: ['company.list'],
        // Rota com AdminOliveGuard: só Admin Oliv-e com a permissão.
        restrito: true
      },
      { label: 'Filiais', route: 'filiais', icon: 'map-pin', permissions: ['subsidiary.list'] },
      { label: 'Centros de Custo', route: 'centro-custos', icon: 'wallet', permissions: ['costcenter.list'] },
      {
        label: 'Parceiros',
        route: 'parceiros',
        icon: 'handshake',
        permissions: [ERoleAdminString.Admin, 'partner.list']
      }
    ]
  },
  {
    title: 'SkinCare',
    items: [
      {
        label: 'Catálogo de Produtos',
        route: 'catalogo-produtos',
        icon: 'sparkles',
        permissions: [ERoleAdminString.Admin, 'skincare.list']
      },
      {
        label: 'Questionário SkinCare',
        route: 'questionario-skincare',
        icon: 'clipboard-list',
        permissions: [ERoleAdminString.Admin, ERoleAdminString.QuestionnaireAdmin, 'skincare.list']
      },
      {
        label: 'Classificação dos Resultados',
        route: 'classificacao-skincare',
        icon: 'sliders-horizontal',
        permissions: [ERoleAdminString.Admin, 'skincare.list']
      },
      {
        label: 'Motor por Kits',
        route: 'motor-kits-skincare',
        icon: 'package',
        permissions: [ERoleAdminString.Admin, 'skincare.list']
      }
    ]
  },
  {
    title: 'Pessoas & Usuários',
    items: [
      {
        label: 'Colaboradores',
        route: 'colaboradores',
        icon: 'user',
        permissions: [ERoleAdminString.Admin, 'employee.list']
      },
      {
        label: 'Médicos',
        route: 'medicos',
        icon: 'stethoscope',
        permissions: [ERoleAdminString.Admin, 'doctor.list']
      },
      {
        label: 'Atendentes',
        route: 'atendentes',
        icon: 'headset',
        permissions: ['attendants.list'],
        // Rota com AdminOliveGuard: só Admin Oliv-e com a permissão.
        restrito: true
      },
      {
        label: 'Usuários Admin',
        route: 'usuarios-admin',
        icon: 'shield',
        permissions: [ERoleAdminString.Admin, 'adminuser.list']
      },
      {
        label: 'Perfis de Acesso',
        route: 'papeis',
        icon: 'user-cog',
        permissions: [ERoleAdminString.Admin, 'role.list']
      }
    ]
  },
  {
    title: 'Configuração de Sistema e Dispositivos',
    items: [
      {
        label: 'Setups',
        route: 'setups',
        icon: 'monitor',
        permissions: [ERoleAdminString.Admin, 'setup.list']
      },
      { label: 'Módulos', route: 'modulos', icon: 'package', permissions: [ERoleAdminString.Admin, 'module.list'] },
      {
        label: 'Dispositivos',
        route: 'dispositivos',
        icon: 'smartphone',
        permissions: [ERoleAdminString.Admin, 'device.list']
      },
      {
        label: 'Temas',
        route: 'temas',
        icon: 'palette',
        permissions: [ERoleAdminString.Admin, 'settings.view']
      }
    ]
  },
  {
    title: 'Questionários',
    items: [
      {
        label: 'Questionários Personalizados',
        route: 'questionarios/personalizados',
        icon: 'clipboard-list',
        permissions: [ERoleAdminString.Admin, ERoleAdminString.QuestionnaireAdmin, 'questionnaire.list']
      }
    ]
  },
  {
    title: 'Pesquisa NPS',
    items: [
      {
        label: 'Pesquisa NPS',
        route: 'pesquisa-nps',
        icon: 'clipboard-check',
        permissions: [ERoleAdminString.Admin]
      }
    ]
  }
];
